import { useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { modesData, transformationCopy } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];
const N = modesData.length;

const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir * 64 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir * -64 }),
};

export const TransformationSection = () => {
  const pinRef = useRef(null);
  const [[activeIdx, dir], setActive] = useState([0, 0]);
  const active = modesData[activeIdx];

  const { scrollYProgress } = useScroll({
    target: pinRef,
    offset: ["start start", "end end"],
  });

  const progressW = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(N - 1, Math.max(0, Math.round(v * (N - 1))));
    setActive(([prev]) => (prev === idx ? [prev, 0] : [idx, idx > prev ? 1 : -1]));
  });

  const goTo = (i) => {
    const el = pinRef.current;
    if (!el) return;
    const scrollable = el.offsetHeight - window.innerHeight;
    window.scrollTo({
      top: el.offsetTop + (i / (N - 1)) * scrollable,
      behavior: "smooth",
    });
  };

  return (
    <section
      id="modes"
      data-testid="transformation-section"
      className="bg-[var(--strollee-dark)] text-[var(--strollee-bg)]"
    >
      <div className="px-6 lg:px-10 pt-20 lg:pt-24 pb-8 lg:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-end justify-between gap-12"
        >
          <div className="max-w-[560px]">
            <p
              data-testid="transformation-eyebrow"
              className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]"
            >
              {transformationCopy.eyebrow}
            </p>
            <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-bg)]">
              {transformationCopy.headlineLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h2>
          </div>
          <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[#A3A199]">
            {transformationCopy.support}
          </p>
        </motion.div>
      </div>

      <div ref={pinRef} className="relative" style={{ height: `${100 + N * 65}vh` }}>
        <div className="sticky top-20 lg:top-24 flex h-[75vh] flex-col items-center justify-center overflow-hidden">
          <div className="w-full px-10 lg:px-16">
            <div className="relative h-[54vh] lg:h-[58vh]">
              <AnimatePresence initial={false} custom={dir}>
                <motion.div
                  key={active.id}
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.55, ease: EASE }}
                  data-testid={`mode-panel-${active.id}`}
                  className="absolute inset-0 grid grid-cols-12 items-center gap-10 lg:gap-14"
                >
                  <div className="col-span-4 max-w-[400px]">
                    <p className="text-sm font-semibold tracking-[0.2em] text-[#A3A199]">
                      {active.number}
                      <span className="mx-2 text-white/25">/</span>
                      <span className="text-white/40">0{N}</span>
                    </p>
                    <h3
                      data-testid="mode-title"
                      className="mt-4 text-[44px] lg:text-[60px] font-semibold leading-[1.02] tracking-[-0.03em] text-[var(--strollee-bg)]"
                    >
                      {active.name}
                    </h3>
                    <p
                      data-testid="mode-tagline"
                      className="mt-4 text-base lg:text-lg leading-relaxed text-[#A3A199]"
                    >
                      {active.tagline}
                    </p>
                    {activeIdx === N - 1 && (
                      <a
                        href="#s1"
                        data-testid="transformation-explore-cta"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-[var(--strollee-bg)] transition-colors duration-200 hover:text-[#8FB89E]"
                      >
                        {transformationCopy.endCta}
                        <ArrowUpRight
                          size={16}
                          strokeWidth={2.2}
                          className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                        />
                      </a>
                    )}
                  </div>
                  <div className="col-span-8">
                    <div className="h-[54vh] lg:h-[58vh] overflow-hidden rounded-[var(--strollee-radius-hero)] border border-white/10 bg-[#22221D]">
                      <img
                        src={active.image.url}
                        alt={active.image.alt}
                        data-testid={`mode-media-${active.id}`}
                        style={active.image.position ? { objectPosition: active.image.position } : undefined}
                        className="h-full w-full object-cover"
                        draggable={false}
                      />
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-7 lg:mt-8 flex items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <span
                  data-testid="transformation-progress-label"
                  className="text-[13px] font-semibold tracking-[0.15em] text-[var(--strollee-bg)]"
                >
                  0{activeIdx + 1} <span className="text-white/35">/ 0{N}</span>
                </span>
                <div className="h-px w-32 bg-white/15">
                  <motion.div
                    data-testid="transformation-progress-line"
                    style={{ width: progressW }}
                    className="h-px bg-[#8FB89E]"
                  />
                </div>
                <div className="flex items-center gap-5">
                  {modesData.map((mode, i) => (
                    <button
                      key={mode.id}
                      data-testid={`transformation-nav-${mode.id}`}
                      onClick={() => goTo(i)}
                      className={`text-[12px] font-medium transition-colors duration-200 ${
                        i === activeIdx
                          ? "text-[var(--strollee-bg)]"
                          : "text-white/35 hover:text-white/70"
                      }`}
                    >
                      {mode.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Previous mode"
                  data-testid="transformation-prev-btn"
                  onClick={() => goTo(Math.max(0, activeIdx - 1))}
                  disabled={activeIdx === 0}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-[var(--strollee-bg)] transition-all duration-200 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  aria-label="Next mode"
                  data-testid="transformation-next-btn"
                  onClick={() => goTo(Math.min(N - 1, activeIdx + 1))}
                  disabled={activeIdx === N - 1}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-[var(--strollee-bg)] transition-all duration-200 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowRight size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
